import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import ReactDOM from 'react-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import RequestImage from '../../../assets/request-sent.svg';
import { PostAsync } from '../../../common/httpRequests';
import { BACKEND } from '../../../constants/settings';
import { uploadState } from '../../../features/pageSlice';
import CodeInput from './CodeInput';
import Loader from 'components/loading/loader';

const schema = yup
  .object({
    code1: yup.number().integer().max(9).min(0).required(),
    code2: yup.number().integer().max(9).min(0).required(),
    code3: yup.number().integer().max(9).min(0).required(),
    code4: yup.number().integer().max(9).min(0).required(),
    code5: yup.number().integer().max(9).min(0).required(),
    code6: yup.number().integer().max(9).min(0).required(),
  })
  .required();
type FormData = yup.InferType<typeof schema>;

export default function CodeForm(props: any) {
  const { email, handleSendEmail } = props;
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [deletedField, setDeletedField] = useState(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const handleVerifyCode = async (data) => {
    setIsLoading(true);
    const code =
      data.code1.toString() +
      data.code2.toString() +
      data.code3.toString() +
      data.code4.toString() +
      data.code5.toString() +
      data.code6.toString();
    const currentCustomerId = window.location.pathname.split('/')[1];
    const request = {
      id: currentCustomerId,
      code: code,
    };
    const res = await PostAsync(`${BACKEND}/api/customerservice/Customer/login_v2`, request);
    if (res.status === 200 && res.data) {
      localStorage.setItem('code', code);
      localStorage.setItem('token', res.data?.token);
      localStorage.setItem('customerId', currentCustomerId);
      localStorage.setItem('date', (Number(new Date().getTime()) / 3.6e6).toString());
      if (!localStorage.getItem('version') && res.data?.version) {
        localStorage.setItem('version', res.data?.version);
      }
      setIsLoading(false);
      dispatch(uploadState());
      navigate(`/${currentCustomerId}/documents`);
    } else {
      setIsLoading(false);
      return enqueueSnackbar(t('email.wrongCode'), {
        variant: 'error',
      });
    }
  };

  const handleDelete = (event) => {
    if (event.code === 'Backspace') {
      let step = Number(event.target.id.slice(-1));
      if (step !== 1 && deletedField === step) {
        (ReactDOM.findDOMNode(document.querySelector(`#code${step - 1}`)) as HTMLElement)?.focus();
      } else if (step !== 1) {
        setDeletedField(step);
      }
    }
  };

  const handleFill = (value) => {
    let step = Number(value.target.id.slice(-1));
    let input = value.target.value;

    const digit = input.length >= 2 ? Number(input[input.length - 1]) : Number(input);

    setValue(`code${step}` as keyof FormData, digit);
    value.target.value = digit;
    if (value.target.value && step !== 6) {
      (ReactDOM.findDOMNode(document.querySelector(`#code${step + 1}`)) as HTMLElement)?.focus();
    } else if (value.target.value) {
      setTimeout(() => {
        document.querySelector('form')?.requestSubmit();
      }, 0);
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    let paste = event.clipboardData.getData('text');
    const separatedNumbers = paste.toString().split('');
    if (separatedNumbers.length >= 6) {
      [...Array(6)].forEach((_, i) => {
        const currentCodeInput = document.querySelector(`#code${i + 1}`);
        if (currentCodeInput) {
          (currentCodeInput as HTMLInputElement).value = separatedNumbers[i];
          setValue(`code${i + 1}` as keyof FormData, Number(separatedNumbers[i]));
          (ReactDOM.findDOMNode(currentCodeInput) as HTMLElement)?.focus();
        }
      });
    }
    setTimeout(() => {
      document.querySelector('form')?.requestSubmit();
    }, 0);
  };

  const handleResend = (e: React.MouseEvent<HTMLButtonElement>) => {
    reset();
    handleSendEmail(e);
  };

  return (
    <Box display={'flex'} flexDirection={'column'} maxWidth="500px">
      <Box component="img" src={RequestImage} alignSelf="center"></Box>
      <form action="/" method="POST" onSubmit={handleSubmit((d) => handleVerifyCode(d))}>
        <DialogTitle fontWeight={600} fontSize={'32px'} display={'flex'} justifyContent={'center'}>
          {t('email.checkEmail')}
        </DialogTitle>
        <DialogContent sx={{ paddingBottom: 0 }}>
          <Typography color={'#7c8a96'}>
            {t('email.checkEmailCtx1')} {email}
            {t('email.checkEmailCtx2')}
          </Typography>
        </DialogContent>
        <DialogContent>
          <Typography color={'#7c8a96'}>
            {errors.code1?.message}
            {errors.code2?.message}
            {errors.code3?.message}
            {errors.code4?.message}
            {errors.code5?.message}
            {errors.code6?.message}
          </Typography>
          <Box display="flex" justifyContent={'space-around'}>
            {isLoading ? (
              <Loader />
            ) : (
              [...Array(6)].map((_, i) => (
                <CodeInput
                  key={i}
                  register={register}
                  position={`code${i + 1}`}
                  handleFill={handleFill}
                  handleDelete={handleDelete}
                  handlePaste={handlePaste}
                />
              ))
            )}
          </Box>
        </DialogContent>
      </form>
      <Typography
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
        paddingLeft={3}
      >
        {t('email.resend')}{' '}
        <Button
          sx={{
            padding: 0,
            paddingLeft: 1,
            textTransform: 'none',
          }}
          onClick={handleResend}
        >
          {t('email.resendLink')}
        </Button>
      </Typography>
    </Box>
  );
}
